/**
 * Granrojo Playground SDK — browser entry.
 *
 * Served as /playground/sdk/granrojo.js. A playground page pulls in the
 * whole kit with one tag:
 *
 *   <script src="/playground/sdk/granrojo.js?v=1"></script>
 *
 * and gets:
 *   - `window.granrojo.bus` — the page message bus ({topic, payload})
 *   - <gr-input> <gr-output>  the page's two endpoints. Put the canvas
 *     inside <gr-output> — it is the picture and every destination:
 *     fullscreen, a chosen physical display, Wi-Fi cast, USB frames, and
 *     the `?output=<name>` perform-mode URL
 *   - <gr-connect>  transport connections (serial / websocket / mqtt)
 *   - <gr-gauge> <gr-toggle> <gr-slider> <gr-chart>  bound widgets
 *   - <gr-monitor>  raw traffic escape hatch
 *   - <gr-link>  topic-to-topic wiring with built-in transforms
 *     (scale / smooth / threshold / throttle — see operators/transforms.ts)
 *   - `window.granrojo.screens` — canvas WebRTC casting, presentation,
 *     frame capture, and acknowledged WebSerial frame output
 *   - `window.granrojo.capabilities` — transport + hardware-API detection
 *   - `window.granrojo.audio` + <gr-audio-output> — shared AudioContext
 *     (auto-unlock) and output-device routing
 *
 * Source layout mirrors the element tiers (see core/base.ts):
 * core/ shared plumbing, adapters/ transports, components/ resource-owning
 * elements, widgets/ bus-bound faces, operators/ headless transforms.
 *
 * Per-element spec (attributes, bus topics, events, when to use):
 * docs/PLAYGROUND_COMPONENTS.md.
 *
 * Custom logic subscribes directly:
 *   granrojo.bus.subscribe("pad/note", (msg) => { ... });
 *   granrojo.bus.publish("matrix/frame", "0101...");
 */

import { MessageBus, topicMatches } from "./core/bus";
import { decodeLine, encodeCommand, LineSplitter } from "./core/codec";
import { parseDescriptor } from "./core/messages";
import { bus } from "./core/runtime";
import { browserCapabilities, transportSupported } from "./core/capabilities";
import { audio } from "./core/audio";
import { availableTransports, createAdapter, registerAdapter } from "./adapters/adapter";
import { screens } from "./core/screens";
import { listScreens } from "./core/displays";

// Adapters and components self-register on import. Components read the
// bus from ./core/runtime, so import order relative to the window.granrojo
// assignment below doesn't matter.
import "./adapters/serial";
import "./adapters/websocket";
import "./adapters/mqtt";
import "./components/gr-connect";
import "./components/gr-input";
import "./components/gr-output";
import "./components/gr-audio-output";
import "./widgets/gr-gauge";
import "./widgets/gr-toggle";
import "./widgets/gr-slider";
import "./widgets/gr-chart";
import "./widgets/gr-monitor";
import "./operators/gr-link";

const SDK_VERSION = "3.0.0";

const api = {
  version: SDK_VERSION,
  bus,
  /** Independent buses keep multiple protocol clients from echoing locally. */
  createBus: () => new MessageBus(),
  topicMatches,
  decodeLine,
  encodeCommand,
  parseDescriptor,
  LineSplitter,
  adapters: { register: registerAdapter, create: createAdapter, available: availableTransports },
  capabilities: { browser: browserCapabilities, transport: transportSupported },
  audio,
  screens,
  displays: { list: listScreens },
};

declare global {
  interface Window {
    granrojo: typeof api;
  }
}

window.granrojo = api;

export type { BusMessage, BusPayload, DeviceDescriptor } from "./core/messages";
export type { BrowserCapabilities } from "./core/capabilities";
export type { AudioOutputDevice, AudioState, AudioStatus } from "./core/audio";
export type {
  CapturedFrame,
  CastOptions,
  CastSession,
  FrameCaptureOptions,
  PresentOptions,
  PresentSession,
  ReceiveOptions,
  ReceiveSession,
  ScreenConnectionState,
  SerialFrameSink,
} from "./core/screens";
export type { ScreenTarget } from "./core/displays";
